import { PageHero, Shell } from '../components';
import { webConfig } from '../config';

interface LegalPageProps {
  kind: 'terms' | 'privacy';
}

const privacySections = [
  [
    '取得する情報',
    'アカウント情報、子どものプロフィール、保存した作品の写真・説明、録音した声、表紙、購入状態、最低限の利用・障害情報を取り扱います。',
  ],
  [
    '作品と声の保存',
    'ゲストのミュージアム・ことば帳の作品は端末に保存されます。アカウント登録後は、その家族の既存作品を含む写真・原音・作品情報をサーバーに自動保存し、参加した家族と同期します。写真ライブラリ全体を送信することはありません。',
  ],
  [
    '録音した声',
    'ことば帳の声や作品についてのお話は、録音した音声として保存します。家族と同期する際も原音のまま保持します。',
  ],
  [
    '保存場所と期間',
    'サーバーの作品情報・写真・声はCloudflareのデータベースと非公開ストレージで管理します。日本国内のみの保管は保証されません。保存中の作品の写真・原音は保持し、削除された作品や未完了の送信で不要になったファイルは定期処理で削除します。',
  ],
  [
    '家族共有',
    '登録後のミュージアム・ことば帳の作品は、同じ家族に参加したメンバーが閲覧・編集できます。作品の削除も家族に反映されます。家族への参加にはアカウント登録と8文字の招待コードが必要です。コードは7日間有効で、1人が参加すると使用済みになり、発行した人は参加前に取り消せます。家族から外れたメンバーは以後サーバー上の作品へアクセスできません。',
  ],
  [
    '利用状況の分析',
    'Firebase Analyticsを使用するバージョンでは、アプリの改善のため、画面の利用、作成・保存操作、購入の状況、アプリのインスタンス識別子、端末・OS・アプリのバージョン、利用時間、IPアドレスから推定されるおおよその地域などをGoogleへ送信します。写真、録音した声、作品名、入力したことば、子どもの名前、メールアドレス、家族・アカウントのIDは分析データに含めません。広告識別子の収集と広告向けの利用は無効にしています。設定の「利用状況を送信」をオフにすると以後の送信を停止し、端末内の分析データをリセットします。すでに送信された集計結果は削除されません。',
  ],
  [
    '削除とお問い合わせ',
    '作品の画面から写真・声を含む作品を削除できます。設定画面からアカウントや家族データの削除を依頼できます。別のアプリなどへ書き出したファイルは自動削除されません。お問い合わせは下記メールアドレスへご連絡ください。',
  ],
];

const termsSections = [
  [
    'サービスの内容',
    'うちのこばこは、子どもの絵・工作・声・ことばを残し、写真から表紙をつくり、家族で保存・共有するサービスです。',
  ],
  [
    'アカウント',
    '端末内の記録はログインせずに利用できます。家族との共有やPremiumの利用には、AppleまたはGoogleでのアカウント登録が必要です。ログイン情報は第三者へ提供せず、招待コードは参加してほしい家族にだけ伝えてください。',
  ],
  [
    '投稿内容',
    '利用者は、記録・共有する作品、写真、文章、音声について必要な権利と同意を得るものとします。第三者の個人情報を不用意に含めないでください。',
  ],
  [
    'サブスクリプション',
    'PremiumはApp StoreまたはGoogle Playを通じて提供します。料金、更新、解約、返金には各ストアの条件が適用されます。',
  ],
  [
    '禁止事項',
    '不正アクセス、他者へのなりすまし、違法・有害なコンテンツの保存、サービスへ過度な負荷を与える行為を禁止します。',
  ],
];

export function LegalPage({ kind }: LegalPageProps) {
  const isPrivacy = kind === 'privacy';
  const sections = isPrivacy ? privacySections : termsSections;
  return (
    <Shell>
      <main className="legal-page">
        <PageHero
          eyebrow={isPrivacy ? 'PRIVACY POLICY' : 'TERMS OF SERVICE'}
          title={isPrivacy ? 'プライバシーポリシー' : '利用規約'}
        >
          <p>制定日：2026年8月5日 ／ 最終更新日：2026年9月{isPrivacy ? '12' : '10'}日</p>
        </PageHero>
        <div className="legal-body">
          <p className="legal-intro">
            {webConfig.legalOperatorName && webConfig.legalAddress
              ? `本サービスは${webConfig.legalOperatorName}（所在地：${webConfig.legalAddress}）が提供します。`
              : 'この文書は開発用の初版です。公開ビルドには提供主体・所在地の確定値が必要です。'}
          </p>
          {sections.map(([title, body], index) => (
            <section key={title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div>
                <h2>{title}</h2>
                <p>{body}</p>
                {isPrivacy && title === '利用状況の分析' ? (
                  <p>
                    Googleによる情報の取り扱いは
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                      Googleのプライバシーポリシー
                    </a>
                    と
                    <a
                      href="https://firebase.google.com/support/privacy"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Firebaseのプライバシー情報
                    </a>
                    をご確認ください。
                  </p>
                ) : null}
              </div>
            </section>
          ))}
          <section>
            <span>{String(sections.length + 1).padStart(2, '0')}</span>
            <div>
              <h2>お問い合わせ</h2>
              <p>
                {webConfig.supportEmail ? (
                  <a href={`mailto:${webConfig.supportEmail}`}>{webConfig.supportEmail}</a>
                ) : (
                  'リリース設定後に掲載します。'
                )}
              </p>
            </div>
          </section>
          {isPrivacy ? (
            <section>
              <span>{String(sections.length + 2).padStart(2, '0')}</span>
              <div>
                <h2>セキュリティ窓口</h2>
                <p>
                  {webConfig.securityEmail ? (
                    <a href={`mailto:${webConfig.securityEmail}`}>{webConfig.securityEmail}</a>
                  ) : (
                    '公開設定後に掲載します。'
                  )}
                </p>
              </div>
            </section>
          ) : null}
        </div>
      </main>
    </Shell>
  );
}
